import { useState, useEffect } from "react";
import { getWoodrowBenchmark } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Trophy, BarChart3, Calendar, Target, Clock } from "lucide-react";

export default function WoodrowBenchmark({ period = "all" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetch = () => {
      getWoodrowBenchmark(period)
        .then(r => { setData(r.data); setLoading(false); })
        .catch(() => setLoading(false));
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, [period]);

  if (loading) {
    return (
      <div className="bg-bt-surface border border-bt-border-subtle p-3 sm:p-4" data-testid="woodrow-benchmark">
        <Skeleton className="h-4 w-40 bg-bt-surface-2 mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-20 bg-bt-surface-2" />
          <Skeleton className="h-20 bg-bt-surface-2" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const beating = data.beating_market;
  const alphaPositive = data.alpha >= 0;
  const marketClosed = data.market_closed;
  const hasSPData = data.spy_return_pct !== 0 || data.spy_price > 0;
  const isNeutral = data.alpha === 0 && data.woodrow_return_pct === 0 && data.spy_return_pct === 0;

  return (
    <div className="bg-bt-surface border border-bt-border-subtle p-3 sm:p-4" data-testid="woodrow-benchmark">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5 text-bt-gold" strokeWidth={1.5} />
          Market Benchmark
        </h3>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-bt-text-tertiary" />
          <span className="font-mono text-[10px] text-bt-text-tertiary">{data.period_label || "All Time"}</span>
        </div>
      </div>

      {/* Alpha Badge */}
      <div className={`flex items-center gap-2 px-3 py-2 mb-3 border ${
        isNeutral
          ? "bg-bt-surface-2 border-bt-border-subtle"
          : beating
            ? "bg-bt-positive/5 border-bt-positive/20"
            : "bg-bt-negative/5 border-bt-negative/20"
      }`} data-testid="benchmark-alpha-badge">
        {isNeutral
          ? <Clock className="w-4 h-4 text-bt-text-tertiary" />
          : beating
            ? <Trophy className="w-4 h-4 text-bt-positive" />
            : <Target className="w-4 h-4 text-bt-negative" />
        }
        <span className={`font-mono text-xs sm:text-sm font-medium ${isNeutral ? "text-bt-text-secondary" : beating ? "text-bt-positive" : "text-bt-negative"}`}>
          {isNeutral ? "Awaiting Market Data" : beating ? "Beating the Market" : "Trailing the Market"}
          {!isNeutral && data.period !== "all" && <span className="text-bt-text-tertiary font-normal"> · {(data.period_label || "").toLowerCase()}</span>}
        </span>
        <span className={`font-mono text-[10px] sm:text-xs ml-auto ${isNeutral ? "text-bt-text-tertiary" : alphaPositive ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-alpha">
          Alpha: {alphaPositive ? "+" : ""}{data.alpha}%
        </span>
      </div>

      {data.period !== "all" && data.all_time_alpha !== undefined && (
        <p className="font-mono text-[10px] text-bt-text-tertiary -mt-1.5 mb-3 px-1" data-testid="benchmark-all-time">
          All time:{" "}
          <span className={data.all_time_beating ? "text-bt-positive" : "text-bt-negative"}>
            {data.all_time_beating ? "beating" : "trailing"} the S&P 500 by {Math.abs(data.all_time_alpha)}%
          </span>{" "}
          (Woodrow {data.all_time_woodrow_pct >= 0 ? "+" : ""}{data.all_time_woodrow_pct}% vs S&P {data.all_time_spy_pct >= 0 ? "+" : ""}{data.all_time_spy_pct}%)
        </p>
      )}

      {/* Comparison Grid */}
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-3">
        {/* Woodrow */}
        <div className="bg-bt-surface-2 border border-bt-border-subtle p-2 sm:p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2 h-0.5 bg-bt-gold inline-block" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-bt-gold">Woodrow</span>
          </div>
          <p className={`font-mono text-base sm:text-lg font-medium ${data.woodrow_return_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-woodrow-return">
            {data.woodrow_return_pct >= 0 ? "+" : ""}{data.woodrow_return_pct}%
          </p>
          <p className="font-mono text-[10px] text-bt-text-tertiary mt-0.5">
            P&L: {data.woodrow_pnl >= 0 ? "+" : ""}${data.woodrow_pnl.toLocaleString()}
          </p>
        </div>
        {/* S&P 500 */}
        <div className="bg-bt-surface-2 border border-bt-border-subtle p-2 sm:p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2 h-0.5 inline-block" style={{ background: "#6B8DE3" }} />
            <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "#6B8DE3" }}>S&P 500</span>
          </div>
          {marketClosed && !hasSPData ? (
            <>
              <p className="font-mono text-sm text-bt-text-tertiary" data-testid="benchmark-spy-return">Market Closed</p>
              <p className="font-mono text-[10px] text-bt-text-tertiary mt-0.5">Opens Mon-Fri 9:30 AM ET</p>
            </>
          ) : (
            <>
              <p className={`font-mono text-base sm:text-lg font-medium ${data.spy_return_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-spy-return">
                {data.spy_return_pct >= 0 ? "+" : ""}{data.spy_return_pct}%
              </p>
              <p className="font-mono text-[10px] text-bt-text-tertiary mt-0.5">
                SPY: ${data.spy_price?.toLocaleString() || "—"}{marketClosed ? " (closed)" : ""}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Annualized Projections */}
      <div className="border border-bt-border-subtle bg-bt-surface-2 p-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-bt-text-tertiary mb-2">
          Annualized Projection (on $100k){data.woodrow_annualized_pct != null && ` · based on ${Math.round(data.days_active)} days`}
        </p>
        {data.woodrow_annualized_pct == null ? (
          <p className="font-mono text-xs text-bt-text-tertiary" data-testid="benchmark-annual-pending">Available after 7 days of trading history</p>
        ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1 mb-1">
              {data.woodrow_annualized_pct >= 0
                ? <TrendingUp className="w-3 h-3 text-bt-positive" />
                : <TrendingDown className="w-3 h-3 text-bt-negative" />
              }
              <span className="font-mono text-[10px] text-bt-gold">Woodrow</span>
            </div>
            <p className={`font-mono text-base font-medium ${data.woodrow_annualized_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-woodrow-annual-pct">
              {data.woodrow_annualized_pct >= 0 ? "+" : ""}{data.woodrow_annualized_pct}%
            </p>
            <p className={`font-mono text-xs ${data.woodrow_annual_earnings >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-woodrow-annual-earnings">
              {data.woodrow_annual_earnings >= 0 ? "+" : ""}${data.woodrow_annual_earnings.toLocaleString()}
            </p>
          </div>
          {(data.spy_annualized_pct !== undefined) && (
          <div>
            <div className="flex items-center gap-1 mb-1">
              {data.spy_annualized_pct >= 0
                ? <TrendingUp className="w-3 h-3 text-bt-positive" />
                : <TrendingDown className="w-3 h-3 text-bt-negative" />
              }
              <span className="font-mono text-[10px]" style={{ color: "#6B8DE3" }}>S&P 500</span>
            </div>
            <p className={`font-mono text-base font-medium ${data.spy_annualized_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-spy-annual-pct">
              {data.spy_annualized_pct >= 0 ? "+" : ""}{data.spy_annualized_pct}%
            </p>
            <p className={`font-mono text-xs ${data.spy_annual_earnings >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="benchmark-spy-annual-earnings">
              {data.spy_annual_earnings >= 0 ? "+" : ""}${data.spy_annual_earnings.toLocaleString()}
            </p>
          </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
